/**
 * SignalWeightsCard — Admin panel to tune the 39 behavioral signal weights
 * that drive Intent Radar's ranking. Every knob is live: adjustments hit
 * `/api/admin/signals/weights`, invalidate the in-service cache and start
 * scoring new events within one 30s TTL window.
 *
 * Signals are grouped for scannability (View / Attention / Consideration /
 * Cart / Return Visit / Search / Session). A delta chip shows how far each
 * override drifts from the code default so admins can spot mistakes fast.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Save, RotateCcw, Radar, Eye, ChevronsUpDown, TrendingUp, TrendingDown, Minus, ArrowUpRight, ArrowDownRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { motion, AnimatePresence } from "framer-motion";

interface SignalRow {
  name: string;
  group: string;
  description: string;
  default_weight: number;
  effective_weight: number;
  has_override: boolean;
}

interface Payload {
  signals: SignalRow[];
  total: number;
  overrides_active: number;
}

interface PreviewBuyer {
  rank: number;
  prev_rank?: number | null;
  rank_delta?: number | null;
  user_id: string;
  name: string;
  country?: string;
  product_id?: string;
  product_title?: string;
  intent_score: number;
  intent_tier: string;
  baseline_intent?: number;
  intent_delta?: number;
  counts_total?: number;
  last_event_type?: string;
}

interface BaselineBuyer {
  rank: number;
  user_id: string;
  name: string;
  country?: string;
  product_id?: string;
  product_title?: string;
  intent_score: number;
  intent_tier: string;
  counts_total?: number;
  last_event_type?: string;
}

interface PreviewSummary {
  sample_size: number;
  top_n: number;
  entered_top: number;
  left_top: number;
  stayed_but_swapped: number;
  unchanged: number;
  changed_weights: number;
}

interface PreviewPayload {
  empty: boolean;
  message?: string;
  baseline: BaselineBuyer[];
  preview: PreviewBuyer[];
  summary?: PreviewSummary;
}

const GROUP_ORDER = [
  "View", "Attention", "Consideration", "Cart", "Return Visit", "Search", "Session",
];

const GROUP_COLOR: Record<string, string> = {
  "View":          "bg-sky-100 text-sky-700 border-sky-200",
  "Attention":     "bg-indigo-100 text-indigo-700 border-indigo-200",
  "Consideration": "bg-amber-100 text-amber-700 border-amber-200",
  "Cart":          "bg-emerald-100 text-emerald-700 border-emerald-200",
  "Return Visit":  "bg-fuchsia-100 text-fuchsia-700 border-fuchsia-200",
  "Search":        "bg-orange-100 text-orange-700 border-orange-200",
  "Session":       "bg-slate-100 text-slate-700 border-slate-200",
};


export const SignalWeightsCard: React.FC = () => {
  const qc = useQueryClient();
  const q = useQuery<Payload>({
    queryKey: ["admin-signal-weights"],
    queryFn: async () => (await api.get("/admin/signals/weights")).data,
  });

  // Local pending edits — only submitted on Save.
  const [pending, setPending] = useState<Record<string, number>>({});

  const save = useMutation({
    mutationFn: async () => (await api.put("/admin/signals/weights", { weights: pending })).data,
    onSuccess: (data: Payload) => {
      toast.success(`${Object.keys(pending).length} signal weight(s) saved`);
      setPending({});
      qc.setQueryData(["admin-signal-weights"], data);
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || "Save failed"),
  });

  const reset = useMutation({
    mutationFn: async () => (await api.post("/admin/signals/weights/reset")).data,
    onSuccess: (data: Payload) => {
      toast.success("All overrides cleared — restored to code defaults");
      setPending({});
      qc.setQueryData(["admin-signal-weights"], data);
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || "Reset failed"),
  });

  const signals = q.data?.signals || [];
  const grouped = useMemo(() => {
    const m: Record<string, SignalRow[]> = {};
    for (const s of signals) {
      (m[s.group] = m[s.group] || []).push(s);
    }
    return m;
  }, [signals]);

  function pendingValue(row: SignalRow): number {
    if (Object.prototype.hasOwnProperty.call(pending, row.name)) return pending[row.name];
    return row.effective_weight;
  }

  function setValue(row: SignalRow, next: number) {
    const clamped = Math.max(-10, Math.min(20, next));
    if (clamped === row.effective_weight) {
      // Reverted to server value — drop from pending
      setPending((p) => {
        const { [row.name]: _drop, ...rest } = p;
        return rest;
      });
    } else {
      setPending((p) => ({ ...p, [row.name]: clamped }));
    }
  }

  const dirtyCount = Object.keys(pending).length;

  // ---------------- Weights A/B Preview ----------------
  // As the admin drags weights, POST the transient map to `preview-rank` (debounced)
  // and render the resulting top-10 buyer shuffle live. Nothing is persisted here.
  const [preview, setPreview] = useState<PreviewPayload | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const debounceRef = useRef<number | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    // Coalesce rapid slider drags into a single call
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(async () => {
      if (abortRef.current) abortRef.current.abort();
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      setPreviewLoading(true);
      try {
        const { data } = await api.post(
          "/admin/signals/weights/preview-rank",
          { weights: pending, top_n: 10 },
          { signal: ctrl.signal as any },
        );
        setPreview(data);
      } catch (e: any) {
        if (e?.name !== "CanceledError" && e?.message !== "canceled") {
          // Non-abort failure — surface once, keep last successful data
          console.warn("Preview rank fetch failed", e);
        }
      } finally {
        if (!ctrl.signal.aborted) setPreviewLoading(false);
      }
    }, 320);
    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(pending), q.data?.overrides_active]);

  if (q.isLoading) {
    return (
      <div className="sg-card p-6 mt-6" data-testid="signal-weights-card">
        <Loader2 className="h-5 w-5 animate-spin text-[color:var(--sg-muted)]" />
      </div>
    );
  }

  return (
    <div className="sg-card p-6 mt-6" data-testid="signal-weights-card">
      <div className="flex items-center justify-between gap-2 flex-wrap mb-1">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-orange-500 to-fuchsia-600 grid place-items-center text-white shrink-0">
            <Radar className="h-4 w-4" />
          </div>
          <div>
            <div className="sg-label">Intent Radar · Signal Weights</div>
            <p className="text-sm text-[color:var(--sg-muted)] max-w-2xl mt-0.5">
              Tune how heavily each of the {signals.length} micro-signals count toward buyer intent.
              Positive weights raise intent; negative weights lower it. Changes apply within 30 seconds.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {q.data?.overrides_active ? (
            <span className="text-[10px] uppercase tracking-widest text-fuchsia-600 font-bold">
              {q.data.overrides_active} override{q.data.overrides_active === 1 ? "" : "s"} active
            </span>
          ) : (
            <span className="text-[10px] uppercase tracking-widest text-emerald-600 font-bold">
              All at defaults
            </span>
          )}
          <Button
            data-testid="signal-weights-reset"
            variant="outline"
            size="sm"
            disabled={reset.isPending || (q.data?.overrides_active === 0 && dirtyCount === 0)}
            onClick={() => reset.mutate()}
          >
            {reset.isPending ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5 mr-1" />}
            Reset all
          </Button>
          <Button
            data-testid="signal-weights-save"
            size="sm"
            disabled={dirtyCount === 0 || save.isPending}
            onClick={() => save.mutate()}
            className="bg-[color:var(--sg-fg)] text-white hover:bg-black"
          >
            {save.isPending ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1" />}
            Save {dirtyCount > 0 && `(${dirtyCount})`}
          </Button>
        </div>
      </div>

      <div className="mt-6 space-y-6">
        {GROUP_ORDER.map((group) => {
          const rows = grouped[group] || [];
          if (rows.length === 0) return null;
          const cls = GROUP_COLOR[group] || "bg-slate-100 text-slate-700 border-slate-200";
          return (
            <div key={group} data-testid={`signal-weights-group-${group.toLowerCase().replace(/\s+/g, "-")}`}>
              <div className="flex items-center gap-2 mb-2">
                <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-widest font-bold ${cls}`}>
                  {group}
                </span>
                <span className="text-[10px] text-[color:var(--sg-muted)]">{rows.length} signals</span>
              </div>
              <div className="grid sm:grid-cols-2 gap-2">
                {rows.map((row) => {
                  const val = pendingValue(row);
                  const dirty = Object.prototype.hasOwnProperty.call(pending, row.name);
                  const delta = val - row.default_weight;
                  const deltaColor = delta > 0 ? "text-emerald-600" : delta < 0 ? "text-rose-600" : "text-slate-400";
                  return (
                    <div
                      key={row.name}
                      data-testid={`signal-row-${row.name}`}
                      className={`rounded-xl border px-3 py-2 flex items-center gap-3 ${
                        dirty ? "border-orange-400 bg-orange-50/60" : "border-[color:var(--sg-border)] bg-white"
                      }`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <code className="text-xs font-mono font-semibold text-slate-800 truncate">
                            {row.name}
                          </code>
                          {row.has_override && !dirty && (
                            <span className="text-[9px] uppercase tracking-widest text-fuchsia-600">override</span>
                          )}
                        </div>
                        <div className="text-[11px] text-[color:var(--sg-muted)] mt-0.5 leading-snug">
                          {row.description}
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <button
                          type="button"
                          onClick={() => setValue(row, val - 1)}
                          data-testid={`signal-dec-${row.name}`}
                          className="h-6 w-6 rounded-md border border-slate-300 text-slate-600 hover:bg-slate-100 grid place-items-center text-xs font-bold"
                          aria-label={`Decrement ${row.name}`}
                        >−</button>
                        <input
                          type="number"
                          min={-10}
                          max={20}
                          value={val}
                          onChange={(e) => setValue(row, parseInt(e.target.value, 10) || 0)}
                          data-testid={`signal-input-${row.name}`}
                          className="h-6 w-12 text-center text-sm font-semibold tabular-nums bg-white border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500/50"
                        />
                        <button
                          type="button"
                          onClick={() => setValue(row, val + 1)}
                          data-testid={`signal-inc-${row.name}`}
                          className="h-6 w-6 rounded-md border border-slate-300 text-slate-600 hover:bg-slate-100 grid place-items-center text-xs font-bold"
                          aria-label={`Increment ${row.name}`}
                        >+</button>
                        <span
                          className={`w-9 text-right text-[10px] font-mono ${deltaColor}`}
                          title={`Default: ${row.default_weight}`}
                        >
                          {delta > 0 ? `+${delta}` : delta}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-6 rounded-xl border border-slate-200 bg-slate-50 dark:bg-slate-800 dark:border-slate-700 p-3 flex items-start gap-2">
        <Eye className="h-4 w-4 text-slate-400 mt-0.5" />
        <div className="text-xs text-slate-600 dark:text-slate-300">
          <span className="font-semibold">Impact preview:</span>{" "}
          The intent score for each (user × product) pair is a sigmoid of the sum of these weights
          across every captured event. Positive events push the intent score up; negative events
          pull it down. Bigger swings on cart / checkout signals produce sharper Intent Radar rankings.
          Fresh events start using the new weights within 30 seconds.
        </div>
      </div>

      <ABPreviewPanel
        preview={preview}
        loading={previewLoading}
        dirtyCount={dirtyCount}
      />
    </div>
  );
};

export default SignalWeightsCard;

// ---------------------------------------------------------------------------
// A/B PREVIEW PANEL — live top-10 rank shuffle as the admin adjusts weights.
// ---------------------------------------------------------------------------

const TIER_STYLE: Record<string, { pill: string; dot: string }> = {
  very_high: { pill: "bg-emerald-100 text-emerald-700 border-emerald-200", dot: "bg-emerald-500" },
  high:      { pill: "bg-teal-100 text-teal-700 border-teal-200",           dot: "bg-teal-500" },
  medium:    { pill: "bg-amber-100 text-amber-700 border-amber-200",        dot: "bg-amber-500" },
  low:       { pill: "bg-orange-100 text-orange-700 border-orange-200",     dot: "bg-orange-500" },
  cold:      { pill: "bg-slate-100 text-slate-600 border-slate-200",        dot: "bg-slate-400" },
};

function RankBadge({ delta }: { delta: number | null | undefined }) {
  if (delta === null || delta === undefined) {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-fuchsia-600" data-testid="rank-badge-new">
        <ArrowUpRight className="h-3 w-3" /> NEW
      </span>
    );
  }
  if (delta === 0) {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] font-bold text-slate-400" data-testid="rank-badge-same">
        <Minus className="h-3 w-3" /> —
      </span>
    );
  }
  if (delta > 0) {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-600" data-testid="rank-badge-up">
        <TrendingUp className="h-3 w-3" /> +{delta}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-[10px] font-bold text-rose-600" data-testid="rank-badge-down">
      <TrendingDown className="h-3 w-3" /> {delta}
    </span>
  );
}

interface PanelProps {
  preview: PreviewPayload | null;
  loading: boolean;
  dirtyCount: number;
}

const ABPreviewPanel: React.FC<PanelProps> = ({ preview, loading, dirtyCount }) => {
  const summary = preview?.summary;

  return (
    <div
      className="mt-6 rounded-2xl border border-slate-200 dark:border-slate-700 bg-gradient-to-br from-white via-white to-fuchsia-50/40 dark:from-slate-900 dark:via-slate-900 dark:to-slate-900 p-4"
      data-testid="weights-ab-preview"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-fuchsia-500 to-indigo-600 grid place-items-center text-white shrink-0 shadow-sm">
            <ArrowUpRight className="h-4 w-4" />
          </div>
          <div>
            <div className="sg-label">Live Rank Shuffle · Top 10 Buyers</div>
            <p className="text-xs text-[color:var(--sg-muted)] max-w-xl mt-0.5">
              Instant sandbox: every knob move re-scores the current buyer pool
              using your transient weights. Nothing is persisted here — hit
              <span className="font-semibold text-slate-700 dark:text-slate-200"> Save </span>
              above to apply. Numbers refresh ~320 ms after your last edit.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {loading ? (
            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-fuchsia-600" data-testid="preview-status-loading">
              <Loader2 className="h-3 w-3 animate-spin" /> recomputing
            </span>
          ) : (
            <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold ${dirtyCount > 0 ? "text-fuchsia-600" : "text-emerald-600"}`} data-testid="preview-status-idle">
              <span className={`h-1.5 w-1.5 rounded-full ${dirtyCount > 0 ? "bg-fuchsia-500 animate-pulse" : "bg-emerald-500"}`} />
              {dirtyCount > 0 ? `previewing ${dirtyCount} tweak${dirtyCount === 1 ? "" : "s"}` : "at current"}
            </span>
          )}
        </div>
      </div>

      {summary && (
        <div className="mt-3 flex items-center gap-1.5 flex-wrap" data-testid="preview-summary-strip">
          <SumChip
            testid="preview-sum-entered"
            label="Entered top"
            value={summary.entered_top}
            tone={summary.entered_top > 0 ? "emerald" : "slate"}
          />
          <SumChip
            testid="preview-sum-left"
            label="Left top"
            value={summary.left_top}
            tone={summary.left_top > 0 ? "rose" : "slate"}
          />
          <SumChip
            testid="preview-sum-swapped"
            label="Reshuffled"
            value={summary.stayed_but_swapped}
            tone={summary.stayed_but_swapped > 0 ? "fuchsia" : "slate"}
          />
          <SumChip
            testid="preview-sum-unchanged"
            label="Unchanged"
            value={summary.unchanged}
            tone="slate"
          />
          <SumChip
            testid="preview-sum-sample"
            label={`sample ${summary.sample_size}`}
            value={""}
            tone="indigo"
          />
        </div>
      )}

      {preview?.empty ? (
        <div
          className="mt-4 rounded-xl border border-dashed border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 p-6 text-center text-sm text-slate-500"
          data-testid="preview-empty-state"
        >
          {preview.message ||
            "No behavioural events yet. Once shoppers browse, the shuffle preview lights up here."}
        </div>
      ) : (
        <div className="mt-3">
          <AnimatePresence initial={false}>
            {(preview?.preview || []).map((row) => {
              const tier = TIER_STYLE[row.intent_tier] || TIER_STYLE.cold;
              const idelta = row.intent_delta || 0;
              return (
                <motion.div
                  key={`${row.user_id}::${row.product_id || ""}`}
                  layout
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ type: "spring", stiffness: 380, damping: 28 }}
                  className="flex items-center gap-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/60 px-3 py-2 mb-1.5"
                  data-testid={`preview-row-${row.rank}`}
                >
                  {/* Rank number */}
                  <div className="w-8 text-right font-mono text-sm font-bold text-slate-500 tabular-nums">
                    #{row.rank}
                  </div>
                  {/* Rank delta */}
                  <div className="w-14 shrink-0">
                    <RankBadge delta={row.rank_delta ?? null} />
                  </div>
                  {/* Name + product */}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-slate-800 dark:text-slate-100 truncate">
                      {row.name}
                    </div>
                    <div className="text-[11px] text-[color:var(--sg-muted)] truncate">
                      {row.product_title}
                      {row.last_event_type ? ` · last: ${row.last_event_type}` : ""}
                    </div>
                  </div>
                  {/* Intent bar */}
                  <div className="w-24 shrink-0 hidden sm:block">
                    <div className="h-1.5 rounded-full bg-slate-100 dark:bg-slate-700 overflow-hidden">
                      <motion.div
                        layout
                        initial={false}
                        animate={{ width: `${row.intent_score}%` }}
                        transition={{ type: "spring", stiffness: 200, damping: 30 }}
                        className={`h-full ${tier.dot}`}
                      />
                    </div>
                    <div className="mt-0.5 flex items-center justify-between text-[10px] tabular-nums">
                      <span className="font-mono font-semibold text-slate-700 dark:text-slate-200">
                        {row.intent_score}
                      </span>
                      {idelta !== 0 && (
                        <span
                          className={`font-mono font-semibold ${
                            idelta > 0 ? "text-emerald-600" : "text-rose-600"
                          }`}
                          data-testid={`preview-intent-delta-${row.rank}`}
                        >
                          {idelta > 0 ? `+${idelta}` : idelta}
                        </span>
                      )}
                    </div>
                  </div>
                  {/* Tier pill */}
                  <span
                    className={`hidden md:inline-flex items-center rounded-full border px-2 py-0.5 text-[9px] font-bold uppercase tracking-widest ${tier.pill}`}
                    data-testid={`preview-tier-${row.rank}`}
                  >
                    {row.intent_tier.replace("_", " ")}
                  </span>
                </motion.div>
              );
            })}
          </AnimatePresence>
          {!preview && !loading && (
            <div className="text-center text-xs text-slate-400 py-6" data-testid="preview-hint">
              Adjust any weight above to preview the shuffle instantly.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

function SumChip({
  testid,
  label,
  value,
  tone,
}: {
  testid: string;
  label: string;
  value: number | string;
  tone: "emerald" | "rose" | "fuchsia" | "slate" | "indigo";
}) {
  const map: Record<string, string> = {
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    rose: "bg-rose-50 text-rose-700 border-rose-200",
    fuchsia: "bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200",
    slate: "bg-slate-50 text-slate-600 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700",
    indigo: "bg-indigo-50 text-indigo-700 border-indigo-200",
  };
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold ${map[tone]}`}
      data-testid={testid}
    >
      {value !== "" && <span className="font-mono tabular-nums">{value}</span>}
      <span className="uppercase tracking-widest text-[9px] opacity-80">{label}</span>
    </span>
  );
}
